import { HttpError } from './http-error'
import type { MindsetRunToken } from '../sdk/mindset-types'
import type { MindsetAuthorizationResponse, MindsetLicence } from './types'

/**
 * The vendor-specific route (`POST /v1/public/mindset/authorize`, docs/decisions.md D-11).
 *
 * Its own client rather than a fourth method on `ScanClient`: the three scan routes are the flow
 * every vendor shares and the assessment contract is written in terms of, and this one exists only
 * because of how one vendor licenses a run. A second vendor with a different handshake gets its own
 * client, not a widened one.
 */
export interface MindsetClient {
  /**
   * Exchanges the SDK's opaque run token for the vendor's licence. The clinic's API key is never
   * here - it is held by the service, which is the entire point of the route.
   *
   * The token is `MindsetRunToken` - opaque - and is put on the wire exactly as the SDK handed it
   * over. It was typed `string` here and in the service's contract, and the SDK hands its
   * authenticator `JSON.parse` of its own token: an object. So every real scan posted
   * `{"runToken":{"config":"…","pubKey":"…"}}` against a contract that required a string, the service
   * answered 400 before its handler ran, and the vendor's WASM died on "Worker not initialized" a
   * moment later (docs/decisions.md D-21).
   */
  authorizeRun(accessToken: string, runToken: MindsetRunToken): Promise<MindsetLicence>

  /**
   * Hands the vendor's own measurement back to this service, to be copied into the vendor's records
   * (docs/decisions.md D-33).
   *
   * **Explicitly not the critical path.** The scan is completed by `submitScan`; this is a second
   * copy of the same measurement going somewhere else, and every caller is expected to ignore
   * whatever it does. It resolves rather than rejects on a failed request for exactly that reason -
   * a caller that has to remember `.catch()` is a caller that will one day forget.
   */
  forwardResult(accessToken: string, result: unknown): Promise<void>
}

export function createMindsetClient(baseUrl: string): MindsetClient {
  return {
    async forwardResult(accessToken, result) {
      try {
        const response = await fetch(`${baseUrl}/v1/public/mindset/results`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(result),
        })

        // Logged and dropped. The service answers 202 whatever became of the forward, so anything
        // else here is the request never arriving - a closed tab, a dead network, a patient already
        // walking away - and none of that is worth a sentence on a screen that says the scan is done.
        if (!response.ok) {
          console.warn('[mindset] the result was not accepted for forwarding', response.status)
        }
      } catch (error) {
        console.warn('[mindset] the result could not be forwarded', error)
      }
    },

    async authorizeRun(accessToken, runToken) {
      const response = await fetch(`${baseUrl}/v1/public/mindset/authorize`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runToken }),
      })

      // No 404 branch, and that is the route's shape rather than an omission: it decides nothing
      // that could be unavailable. Whether this scan is still live was settled by GET
      // /v1/public/scan and POST /v1/public/scan/start, both of which ran before the SDK existed.
      if (!response.ok) {
        throw new HttpError(
          response.status,
          `mindset authorize failed with ${response.status}`,
        )
      }

      const body = (await response.json()) as MindsetAuthorizationResponse
      return body.vitalsAuth
    },
  }
}
